import { Dialog, DialogContent, DialogTitle, Fab, makeStyles, Theme, useMediaQuery } from '@material-ui/core'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import ContentAdd from '@material-ui/icons/AddOutlined'
import AlertError from '@material-ui/icons/ErrorOutlineOutlined'
import classnames from 'classnames'
import { authSelectors } from 'ducks/auth'
import { orgSelectors } from 'ducks/orgs'
import { BetaChip } from 'elements/chip/BetaChip'
import useTrackComponent from 'hooks/eventTracking/useTrackComponent'
import { Button } from 'opensolar-ui'
import React, { FC, useCallback, useMemo, useState } from 'react'
import { SaveButton, Toolbar, useNotify, useRefresh, useTranslate } from 'react-admin'
import { useSelector } from 'react-redux'
import { ActionType, EventType } from 'types/tracking'
import { useFeatureFlag } from 'util/featureFlags'
import ProposalTemplateCreateModal, { BESPOKE_REQUEST_URL, ProposalTemplateOption } from './ProposalTemplateCreateModal'
import { ProposalTemplateCreate } from './ProposalTemplates'
import { SALES_MACHINE_DEMO_URL } from './salesMachineDemoUrl'
import { ProposalTemplateTypeEnum } from './types'
import {
  TEST_ID_PROPOSAL_TEMPLATE_BESPOKE_BUTTON,
  TEST_ID_PROPOSAL_TEMPLATE_BESPOKE_V2_BUTTON,
} from 'e2e/playwrightTestIds'

const useStyles = makeStyles(
  () => ({
    floating: {
      margin: 0,
      top: 'auto',
      right: 20,
      bottom: 110,
      left: 'auto',
      position: 'fixed',
      zIndex: 1000,
    },
  }),
  { name: 'ProposalTemplateCreateButton' }
)

type DialogPropsType = {
  dialogMode: string
  setDialogMode: Function
}

const CustomizedToolbar = (props: any) => {
  const translate = useTranslate()
  const notify = useNotify()
  const refresh = useRefresh()
  const { trackEvent } = useTrackComponent({
    componentKey: 'proposal_template.toolbar',
    eventName: 'User Viewed Proposal Template Creation Form',
  })
  const onSuccess = useCallback((response) => {
    const record = response.data
    if (record) {
      trackEvent(
        EventType.SYSTEM_EVENT,
        { type: ActionType.SAVE },
        { eventName: 'User Created Proposal Template', title: record.title }
      )
      notify('ra.notification.created', 'info')
      props.setDialogMode('')
      refresh()
    }
  }, [])
  return (
    <Toolbar
      style={{ zIndex: 1, left: 0, position: 'absolute', bottom: 0, boxSizing: 'border-box', width: '100%' }}
      {...props}
    >
      <div>
        <Button
          variant="contained"
          color="default"
          style={{ margin: 10 }}
          onClick={() => props.setDialogMode('')}
          startIcon={<AlertError />}
          size="large"
        >
          <span>{translate('Cancel')}</span>
        </Button>
        <SaveButton
          {...props}
          submitOnEnter={true}
          onSave={(values: object, redirect: string, form: any, defaultSaveFunc: Function) => {
            defaultSaveFunc(values, redirect, { onSuccess })
          }}
        />
      </div>
    </Toolbar>
  )
}

const ProposalTemplateDialog: FC<DialogPropsType> = ({ dialogMode, setDialogMode }) => {
  const translate = useTranslate()
  return (
    <div>
      <Dialog id="ProposalTemplateDialog" open={!!dialogMode}>
        <DialogTitle>{translate('Create Proposal Template')}</DialogTitle>
        <DialogContent style={{ padding: '0 20px 70px' }}>
          <ProposalTemplateCreate
            resource="proposal_templates"
            basePath="/proposal_templates/create"
            mode={dialogMode}
            hideBreadCrumbs={true}
            hideTitle={true}
            noBorder={true}
            toolbar={<CustomizedToolbar setDialogMode={setDialogMode} />}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}

type MenuOption = {
  chip?: React.ReactNode
  mode: ProposalTemplateTypeEnum
  name: string
}

type CreateOption = ProposalTemplateOption & {
  legacyMenu?: MenuOption
  testId?: string
}

const BESPOKE_TEMPLATE_IMG = `${window.PUBLIC_URL}/images/proposal/template_bespoke.png`
const CLASSIC_TEMPLATE_IMG = `${window.PUBLIC_URL}/images/proposal/template_classic.png`
const DEFAULT_TEMPLATE_IMG = `${window.PUBLIC_URL}/images/proposal/template_default.png`

const useProposalTemplateOptions = (): CreateOption[] => {
  const api_key_pvsell = useSelector(authSelectors.getPvSellKey)
  const bespoke_enabled = useSelector((state: any) => orgSelectors.getOrg(state)?.enable_bespoke_proposal)
  const isStaff = useSelector(authSelectors.getIsStaff)
  const enableProposalV2 = useFeatureFlag('enable_sales_machine', 'on')

  return useMemo(() => {
    const list: CreateOption[] = []

    if (enableProposalV2) {
      list.push({
        buttonText: 'Demo',
        cardBody: 'Slide-based interactive lifestyle proposal',
        cardImgSrc: DEFAULT_TEMPLATE_IMG,
        cardTitle: 'Swipe',
        id: 'proposalV2',
        legacyMenu: {
          mode: ProposalTemplateTypeEnum.proposalV2_grapes_swipe,
          name: 'Sales Machine Swipe Proposal',
        },
        mode: ProposalTemplateTypeEnum.proposalV2_grapes_swipe,
        url: SALES_MACHINE_DEMO_URL,
      })
    }

    if (isStaff) {
      list.push({
        cardBody: 'Non-solar proposal template for staff use',
        cardImgSrc: DEFAULT_TEMPLATE_IMG,
        cardTitle: 'Non Solar Proposal',
        id: 'nonsolar',
        legacyMenu: {
          chip: <BetaChip size="xSmall" />,
          mode: ProposalTemplateTypeEnum.proposalV2_nonsolar,
          name: 'Non Solar Proposal',
        },
        mode: ProposalTemplateTypeEnum.proposalV2_nonsolar,
      })
    }

    list.push({
      cardBody: 'Long-form document style "Classic" proposal',
      cardImgSrc: CLASSIC_TEMPLATE_IMG,
      cardTag: 'Classic',
      cardTitle: 'Scroll',
      id: 'classic',
      legacyMenu: { mode: ProposalTemplateTypeEnum.opensolar, name: 'Classic Template' },
      mode: ProposalTemplateTypeEnum.opensolar,
    })

    if (Boolean(api_key_pvsell)) {
      list.push({
        cardBody: 'PVProsell proposal template.',
        cardImgSrc: DEFAULT_TEMPLATE_IMG,
        cardTitle: 'PVProsell Template',
        id: 'pvprosell',
        legacyMenu: { mode: ProposalTemplateTypeEnum.pvprosell, name: 'PVProsell Template' },
        mode: ProposalTemplateTypeEnum.pvprosell,
      })
    }

    if (Boolean(bespoke_enabled)) {
      list.push({
        cardBody: 'Your custom branded proposal',
        cardImgSrc: BESPOKE_TEMPLATE_IMG,
        cardTitle: 'Bespoke',
        id: 'bespoke',
        testId: TEST_ID_PROPOSAL_TEMPLATE_BESPOKE_BUTTON,
        legacyMenu: { mode: ProposalTemplateTypeEnum.bespoke, name: 'Bespoke Template' },
        mode: ProposalTemplateTypeEnum.bespoke,
      })
      list.push({
        cardBody: 'Your custom branded proposal v2',
        cardImgSrc: BESPOKE_TEMPLATE_IMG,
        cardTitle: 'Bespoke v2',
        id: 'bespoke_v2',
        testId: TEST_ID_PROPOSAL_TEMPLATE_BESPOKE_V2_BUTTON,
        legacyMenu: {
          chip: <BetaChip size="xSmall" />,
          mode: ProposalTemplateTypeEnum.proposalV2_bespoke,
          name: 'Bespoke v2 Template',
        },
        mode: ProposalTemplateTypeEnum.proposalV2_bespoke,
      })
    } else {
      list.push({
        buttonText: 'Send Request',
        cardBody: 'Need something more tailored for you?',
        cardImgSrc: BESPOKE_TEMPLATE_IMG,
        cardTitle: 'Bespoke',
        id: 'bespoke',
        mode: ProposalTemplateTypeEnum.bespoke,
        url: BESPOKE_REQUEST_URL,
      })
    }

    return list
  }, [api_key_pvsell, bespoke_enabled, enableProposalV2, isStaff])
}

const CreateButton = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const translate = useTranslate()
  const [dialogMode, setDialogMode] = useState<string>('')
  const [pendingTemplateMode, setPendingTemplateMode] = useState<ProposalTemplateTypeEnum | null>(null)
  const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
  const classes = useStyles()
  const open = Boolean(anchorEl)
  const enableNewModal = useFeatureFlag('enable_classic_proposal_edit_preview', 'on')

  const options = useProposalTemplateOptions()
  const legacyMenuOptions = useMemo(
    () => options.map((option) => option.legacyMenu).filter((option): option is MenuOption => Boolean(option)),
    [options]
  )

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (enableNewModal) {
      setModalOpen(true)
      return
    }
    if (legacyMenuOptions.length > 1) {
      setAnchorEl(event.currentTarget)
    } else if (legacyMenuOptions.length === 1) {
      setDialogMode(legacyMenuOptions[0].mode)
    }
  }
  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handlePickerTransitionExited = () => {
    if (pendingTemplateMode !== null) {
      setDialogMode(pendingTemplateMode)
      setPendingTemplateMode(null)
    }
  }

  const handleSelectMode = (mode: ProposalTemplateTypeEnum) => {
    setPendingTemplateMode(mode)
    setModalOpen(false)
  }

  return (
    <div>
      {isSmall ? (
        <Fab
          id="pvprosell-create-button"
          aria-controls="pvprosell-create-menu"
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          //@ts-ignore
          // variant="contained"
          style={{
            width: 70,
            height: 70,
            borderRadius: '50%',
          }}
          color="default"
          className={classnames(classes.floating)}
          aria-label="Add Proposal Template"
        >
          <ContentAdd />
        </Fab>
      ) : (
        <Button
          id="pvprosell-create-button"
          aria-controls="pvprosell-create-menu"
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          variant="outlined"
          color="default"
          startIcon={<ContentAdd />}
          size="large"
        >
          <span>{translate('Proposal Template')}</span>
        </Button>
      )}
      {!enableNewModal && (
        <Menu
          id="pvprosell-create-menu"
          aria-labelledby="pvprosell-create-button"
          anchorEl={anchorEl}
          getContentAnchorEl={null}
          open={open}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          {legacyMenuOptions.map((option) => {
            const createOption = options.find((item) => item.legacyMenu?.mode === option.mode)
            return (
            <MenuItem
              key={option.mode}
              {...(createOption?.testId && { 'data-testid': createOption.testId })}
              onClick={() => {
                handleMenuClose()
                setDialogMode(option.mode)
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {translate(option.name)}
                {option.chip}
              </div>
            </MenuItem>
            )
          })}
        </Menu>
      )}
      {enableNewModal && (
        <ProposalTemplateCreateModal
          onClose={() => setModalOpen(false)}
          onSelectMode={handleSelectMode}
          onTransitionExited={handlePickerTransitionExited}
          open={modalOpen}
          options={options}
        />
      )}
      <ProposalTemplateDialog dialogMode={dialogMode} setDialogMode={setDialogMode} />
    </div>
  )
}

export default CreateButton
